import { normalizeTrailingSlash } from "~/constants/locale";

/** Decode HTML entities. Uses DOM in browser; regex fallback for SSR. */
const decodeHtmlEntities = (str) => {
  if (typeof document !== "undefined") {
    const el = document.createElement("div");
    el.innerHTML = str;
    return el.textContent || el.innerText || "";
  }
  // SSR fallback: common entities + numeric (&#1234; &#x1a2b;)
  const entities = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };
  return str.replace(/&(?:amp|lt|gt|quot|apos|nbsp);|&#(\d+);|&#x([0-9a-f]+);/gi, (m, dec, hex) =>
    dec != null
      ? String.fromCharCode(parseInt(dec, 10))
      : hex != null
        ? String.fromCharCode(parseInt(hex, 16))
        : (entities[m.slice(1, -1).toLowerCase()] ?? m)
  );
};

const cleanSchemaText = (html) => {
  if (!html) return "";
  // Type guard: answer_text may be string (help-and-advice-category) or array (faqs-topic).
  // If data shape changes, passing object/array would throw on .replace(). Return empty instead.
  if (typeof html !== "string") return "";

  const text = html
    .replace(/<\/(p|div|li|h[1-6]|blockquote|section|article)>/gi, ". ")
    .replace(/<br\s*\/?>/gi, ". ")
    .replace(/<[^>]*>/g, " ") // remove tags
    .replace(/\s+/g, " ")
    .replace(/ \. /g, " ")
    .trim();

  return decodeHtmlEntities(text);
};

export const generateSchema = (page, data) => {
  let itemLists = [];

  if (page == "breadcrumbs") {
    if (data.length > 0) {
      data.forEach(function (item, index) {
        let listItem = {
          "@type": "ListItem",
          position: index + 1,
          name: cleanSchemaText(item.title),
        };
        if (item.to) {
          const path = localeUri(item.to)?.fullPath;
          if (path) listItem["item"] = useRuntimeConfig().public.i18n.baseUrl + normalizeTrailingSlash(path);
        }
        itemLists.push(listItem);
      });
    }
  } else if (page == "faqs-topic" || page == "test-detail") {
    if (data.length > 0) {
      data.forEach(function (item, index) {
        // Only "text" chunks are concatenated; other answer.type values (e.g. lists, media)
        // are skipped. If future types contain HTML-adjacent content, update this filter
        // and ensure cleanSchemaText handles it correctly.
        let answerText = "";
        item.answer_text.forEach(function (answer, answerIndex) {
          if (answer.type == "text") {
            answerText += answer.text;
          }
        });

        let listItem = {
          "@type": "Question",
          name: cleanSchemaText(item.title),
          acceptedAnswer: {
            "@type": "Answer",
            text: cleanSchemaText(answerText),
          },
        };
        itemLists.push(listItem);
      });
    }
  } else if (page == "city" || page == "help-and-advice-category") {
    if (data.length > 0) {
      data.forEach(function (item, index) {
        let question = "";
        let answer = "";
        if (page == "help-and-advice-category") {
          question = cleanSchemaText(item.title);
          answer = cleanSchemaText(item.answer_text);
        } else {
          question = cleanSchemaText(item.title_text);
          answer = cleanSchemaText(item.body_text);
        }

        let listItem = {
          "@type": "Question",
          name: question,
          acceptedAnswer: {
            "@type": "Answer",
            text: answer,
          },
        };
        itemLists.push(listItem);
      });
    }
  }

  if (itemLists.length > 0) {
    let schemaHTML = {};
    if (page == "breadcrumbs") {
      schemaHTML = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: itemLists,
      };
    } else {
      schemaHTML = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: itemLists,
      };
    }

    let scripts = [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify(schemaHTML),
      },
    ];

    useHead({
      script: scripts,
    });
  }
};
